export interface GeolocationResult {
  city: string
  state: string
  latitude: number
  longitude: number
}

export async function getCurrentLocation(): Promise<GeolocationResult | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        
        try {
          // Use reverse geocoding with retry fallback
          let data: any = null
          
          // Try OpenStreetMap first (free, but rate limited)
          try {
            const osmResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=pt-BR`,
              { headers: { 'User-Agent': 'DoeApp/1.0' } }
            )
            if (osmResponse.ok) {
              data = await osmResponse.json()
            }
          } catch (osmError) {
            console.warn('OSM geocoding failed, trying fallback:', osmError)
          }
          
          // Fallback to BigDataCloud (no API key needed, more reliable)
          if (!data) {
            const bdcResponse = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`
            )
            data = await bdcResponse.json()
          }
          
          const city = data.address?.city || data.address?.town || data.address?.village || data.city || data.locality || ''
          const state = data.address?.state || data.principalSubdivision || ''
          
          // Convert state name to abbreviation
          const stateAbbrev = getStateAbbreviation(state)
          
          resolve({
            city,
            state: stateAbbrev,
            latitude,
            longitude
          })
        } catch (error) {
          console.error('Geocoding error:', error)
          resolve({ city: '', state: '', latitude, longitude })
        }
      },
      (error) => {
        console.error('Geolocation error:', error)
        resolve(null)
      },
      { timeout: 10000, enableHighAccuracy: false }
    )
  })
}

// Alias for compatibility
export const detectLocation = getCurrentLocation

function getStateAbbreviation(stateName: string): string {
  const stateMap: Record<string, string> = {
    'acre': 'AC',
    'alagoas': 'AL',
    'amapá': 'AP',
    'amazonas': 'AM',
    'bahia': 'BA',
    'ceará': 'CE',
    'distrito federal': 'DF',
    'espírito santo': 'ES',
    'goiás': 'GO',
    'maranhão': 'MA',
    'mato grosso': 'MT',
    'mato grosso do sul': 'MS',
    'minas gerais': 'MG',
    'pará': 'PA',
    'paraíba': 'PB',
    'paraná': 'PR',
    'pernambuco': 'PE',
    'piauí': 'PI',
    'rio de janeiro': 'RJ',
    'rio grande do norte': 'RN',
    'rio grande do sul': 'RS',
    'rondônia': 'RO',
    'roraima': 'RR',
    'santa catarina': 'SC',
    'são paulo': 'SP',
    'sergipe': 'SE',
    'tocantins': 'TO'
  }
  
  return stateMap[stateName.toLowerCase()] || stateName
}
